import React from "react";
import axios from "axios";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// react-chartjs-2 + chart.js
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

function Analytics() {
  const [range, setRange] = React.useState("daily");
  const [isLoading, setIsLoading] = React.useState(true);
  const [periodOffset, setPeriodOffset] = React.useState(0);
  const [analyticsData, setAnalyticsData] = React.useState({
    dateRange: null,
    topActiveUsers: [],
    topUsedTags: [],
    conversion: { trialUsers: 0, paidUsers: 0, trialToPaidConversionRate: 0 },
    topFeaturesUsed: [],
    notes: {},
  });

  const handleRange = (_event, nextRange) => {
    if (nextRange) setRange(nextRange);
  };

  React.useEffect(() => {
    const fetchAnalyticsOverview = async () => {
      setIsLoading(true);
      const token =
        localStorage.getItem("dsf_token") ||
        localStorage.getItem("token") ||
        localStorage.getItem("accessToken") ||
        "";
      const apiBaseUrl = (
        process.env.REACT_APP_API_BASE_URL || "https://dsf-api.classicprogrammers.com/api/v1/admin"
      ).replace(/\/+$/, "");
      const normalizedPeriod = range;
      const endpoint = `/analytics/overview/?period=${encodeURIComponent(normalizedPeriod)}`;
      const requestUrls = [];
      if (apiBaseUrl) requestUrls.push(`${apiBaseUrl}${endpoint}`);

      try {
        let response;
        for (const url of requestUrls) {
          try {
            response = await axios.get(url, {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            break;
          } catch (_requestError) {
            // Try next URL candidate
          }
        }

        const payload = response?.data?.data || response?.data || {};
        setAnalyticsData({
          dateRange: payload.dateRange || null,
          topActiveUsers: Array.isArray(payload.topActiveUsers) ? payload.topActiveUsers : [],
          topUsedTags: Array.isArray(payload.topUsedTags) ? payload.topUsedTags : [],
          conversion:
            payload.conversion && typeof payload.conversion === "object"
              ? payload.conversion
              : { trialUsers: 0, paidUsers: 0, trialToPaidConversionRate: 0 },
          topFeaturesUsed: Array.isArray(payload.topFeaturesUsed) ? payload.topFeaturesUsed : [],
          notes: payload.notes || {},
        });
      } catch (_error) {
        setAnalyticsData((prev) => ({ ...prev }));
      } finally {
        setIsLoading(false);
      }
    };

    fetchAnalyticsOverview();
  }, [range]);

  const periodLabel = React.useMemo(() => {
    const start = analyticsData.dateRange?.start ? new Date(analyticsData.dateRange.start) : null;
    const end = analyticsData.dateRange?.end ? new Date(analyticsData.dateRange.end) : null;
    if (start && end && !Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
      const fmt = new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
      return `${fmt.format(start)} - ${fmt.format(end)}`;
    }

    if (range === "monthly")
      return periodOffset === 0 ? "Current month" : `Month offset ${periodOffset}`;
    if (range === "annually")
      return periodOffset === 0 ? "Current year" : `Year offset ${periodOffset}`;
    return periodOffset === 0 ? "Today" : `Day offset ${periodOffset}`;
  }, [analyticsData.dateRange, range, periodOffset]);

  const trialToPaidChart = React.useMemo(() => {
    const labels = ["Trial Users", "Paid Users"];
    const source = [
      Number(analyticsData.conversion?.trialUsers || 0),
      Number(analyticsData.conversion?.paidUsers || 0),
    ];

    return {
      data: {
        labels,
        datasets: [
          {
            label: "Trial to Paid",
            data: source,
            tension: 0.4,
            pointRadius: 2.5,
            pointHoverRadius: 4,
            pointHitRadius: 12,
            pointBackgroundColor: "rgba(229, 10, 70, 1)",
            pointBorderColor: "rgba(229, 10, 70, 1)",
            pointBorderWidth: 2,
            borderWidth: 3,
            borderColor: "rgba(229, 10, 70, 1)",
            backgroundColor: (context) => {
              const { chart } = context;
              const { ctx, chartArea } = chart;
              if (!chartArea) return "transparent";

              const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
              gradient.addColorStop(0, "rgba(229, 10, 70, 0.45)");
              gradient.addColorStop(1, "rgba(229, 10, 70, 0.00)");
              return gradient;
            },
            fill: true,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        interaction: { intersect: false, mode: "index" },
        scales: {
          y: {
            grid: { drawBorder: false, color: "rgba(0,0,0,0.04)" },
            ticks: { display: false },
          },
          x: {
            grid: { display: false, drawBorder: false },
            ticks: { display: false },
          },
        },
      },
    };
  }, [analyticsData.conversion]);

  const activeUsers = React.useMemo(
    () =>
      analyticsData.topActiveUsers.map((user) => {
        const initials =
          user?.name
            ?.split(" ")
            .map((p) => p[0])
            .join("")
            .slice(0, 2)
            .toUpperCase() || "U";
        const lastActive = user?.lastActiveAt ? new Date(user.lastActiveAt) : null;
        const lastActiveLabel =
          lastActive && !Number.isNaN(lastActive.getTime())
            ? new Intl.DateTimeFormat("en-GB", {
                day: "2-digit",
                month: "short",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
              }).format(lastActive)
            : "—";

        return {
          id: user?._id || user?.email || initials,
          name: user?.name || "User",
          email: user?.email || "—",
          avatar: user?.profilePhoto || "",
          initials,
          badges: [`Usage: ${Number(user?.usageScore || 0)}`, `Last active: ${lastActiveLabel}`],
        };
      }),
    [analyticsData.topActiveUsers]
  );

  const tags = React.useMemo(
    () =>
      analyticsData.topUsedTags.map((tag) => ({
        key: tag?._id || tag?.name,
        label: `${tag?.name || "Tag"} (${Number(tag?.usageCount || 0)})`,
      })),
    [analyticsData.topUsedTags]
  );

  const featureUsage = React.useMemo(() => {
    const list = analyticsData.topFeaturesUsed.map((feature) => ({
      name: feature?.feature || "Feature",
      usageCount: Number(feature?.usageCount || 0),
    }));
    const max = Math.max(...list.map((f) => f.usageCount), 0);
    return list.map((f) => ({
      ...f,
      usage: max > 0 ? Math.round((f.usageCount / max) * 100) : 0,
    }));
  }, [analyticsData.topFeaturesUsed]);

  const sectionTitleSx = {
    mb: 1,
    minHeight: "26px",
    display: "flex",
    alignItems: "center",
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox display="flex" justifyContent="flex-start" mb={2}>
          <ToggleButtonGroup
            value={range}
            exclusive
            onChange={handleRange}
            sx={{
              "& .MuiToggleButton-root": {
                textTransform: "none",
                border: 0,
                px: 2,
                py: 0.75,
                borderRadius: "0.5rem !important",
                mr: 1,
                backgroundColor: "#9e9e9e",
                color: "#fff",
              },
              "& .MuiToggleButton-root.Mui-selected": {
                backgroundColor: "rgba(229, 10, 70, 1)",
                color: "#fff",
              },
              "& .MuiToggleButton-root.Mui-selected:hover": {
                backgroundColor: "rgba(229, 10, 70, 0.9)",
              },
            }}
          >
            <ToggleButton value="daily">Daily</ToggleButton>
            <ToggleButton value="monthly">Monthly</ToggleButton>
            <ToggleButton value="annually">Annually</ToggleButton>
          </ToggleButtonGroup>
        </MDBox>

        <Card sx={{ mb: 3 }}>
          <MDBox p={2} display="flex" alignItems="center" justifyContent="space-between">
            <IconButton
              onClick={() => setPeriodOffset((p) => p - 1)}
              sx={{
                backgroundColor: "rgba(22, 28, 45, 1)",
                color: "#fff",
                "&:hover": { backgroundColor: "rgba(22, 28, 45, 0.9)" },
              }}
            >
              <Icon>chevron_left</Icon>
            </IconButton>
            <MDTypography variant="button" fontWeight="bold">
              {periodLabel}
            </MDTypography>
            <IconButton
              onClick={() => setPeriodOffset((p) => p + 1)}
              sx={{
                backgroundColor: "rgba(22, 28, 45, 1)",
                color: "#fff",
                "&:hover": { backgroundColor: "rgba(22, 28, 45, 0.9)" },
              }}
            >
              <Icon>chevron_right</Icon>
            </IconButton>
          </MDBox>
        </Card>

        {isLoading ? (
          <Card sx={{ mb: 2 }}>
            <MDBox py={8} display="flex" alignItems="center" justifyContent="center" gap={1.5}>
              <CircularProgress size={20} sx={{ color: "rgba(229, 10, 70, 1)" }} />
              <MDTypography variant="button" color="text">
                Loading analytics...
              </MDTypography>
            </MDBox>
          </Card>
        ) : null}

        {!isLoading ? (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <MDTypography variant="h6" fontWeight="bold" sx={sectionTitleSx}>
                  Detailed Analytics:
                </MDTypography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Card>
                      <MDBox p={2}>
                        <MDBox
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          mb={1}
                        >
                          <MDTypography variant="button" fontWeight="bold">
                            Trial To Paid
                          </MDTypography>
                          <MDBox display="flex" alignItems="center" gap={1}>
                            <MDBox display="flex" alignItems="center" gap={0.5}>
                              <MDBox
                                width="0.5rem"
                                height="0.5rem"
                                borderRadius="50%"
                                sx={{ backgroundColor: "rgba(229, 10, 70, 1)" }}
                              />
                              <MDTypography variant="caption" color="text">
                                Trial
                              </MDTypography>
                            </MDBox>
                            <MDBox display="flex" alignItems="center" gap={0.5}>
                              <MDBox
                                width="0.5rem"
                                height="0.5rem"
                                borderRadius="50%"
                                sx={{ backgroundColor: "rgba(0, 0, 0, 0.18)" }}
                              />
                              <MDTypography variant="caption" color="text">
                                Paid
                              </MDTypography>
                            </MDBox>
                          </MDBox>
                        </MDBox>
                        <MDBox height="8rem">
                          <Line
                            data={trialToPaidChart.data}
                            options={trialToPaidChart.options}
                            redraw
                          />
                        </MDBox>
                        <MDBox
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          mt={1}
                        >
                          <MDTypography variant="caption" color="text">
                            Conversion Rate
                          </MDTypography>
                          <MDTypography
                            variant="caption"
                            fontWeight="bold"
                            sx={{ color: "rgba(229, 10, 70, 1)" }}
                          >
                            {`${Number(
                              analyticsData.conversion?.trialToPaidConversionRate || 0
                            ).toFixed(2)}%`}
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                    </Card>
                  </Grid>

                  <Grid item xs={12}>
                    <MDTypography variant="h6" fontWeight="bold" sx={sectionTitleSx}>
                      Feature Usage:
                    </MDTypography>
                    <Card>
                      <MDBox p={2}>
                        <MDTypography variant="caption" color="text" display="block" mb={1.5}>
                          Daily feature adoption by active users.
                        </MDTypography>
                        <MDBox display="flex" flexDirection="column" gap={1.25}>
                          {featureUsage.map((feature) => (
                            <MDBox key={feature.name}>
                              <MDBox display="flex" justifyContent="space-between" mb={0.35}>
                                <MDTypography variant="caption" fontWeight="medium">
                                  {feature.name}
                                </MDTypography>
                                <MDTypography variant="caption" color="text">
                                  {`${feature.usageCount} uses`}
                                </MDTypography>
                              </MDBox>
                              <MDBox
                                sx={{
                                  height: "0.45rem",
                                  width: "100%",
                                  borderRadius: "0.5rem",
                                  backgroundColor: "rgba(52, 71, 103, 0.1)",
                                  overflow: "hidden",
                                }}
                              >
                                <MDBox
                                  sx={{
                                    height: "100%",
                                    width: `${feature.usage}%`,
                                    borderRadius: "0.5rem",
                                    backgroundColor: "rgba(229, 10, 70, 1)",
                                  }}
                                />
                              </MDBox>
                            </MDBox>
                          ))}
                        </MDBox>
                        {analyticsData.notes?.contentVideos ? (
                          <MDTypography variant="caption" color="text" display="block" mt={1.5}>
                            {analyticsData.notes.contentVideos}
                          </MDTypography>
                        ) : null}
                      </MDBox>
                    </Card>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} md={6}>
                <MDTypography variant="h6" fontWeight="bold" sx={sectionTitleSx}>
                  Top Active Users:
                </MDTypography>

                <MDBox display="flex" flexDirection="column" gap={2}>
                  {activeUsers.map((user) => (
                    <Card key={user.id} sx={{ border: "1px solid rgba(229, 10, 70, 0.35)" }}>
                      <MDBox p={2} display="flex" alignItems="center" gap={2}>
                        {user.avatar ? (
                          <MDAvatar src={user.avatar} alt={user.name} size="md" />
                        ) : (
                          <MDAvatar
                            size="md"
                            sx={{
                              backgroundColor: "rgba(229, 10, 70, 1) !important",
                              color: "#fff",
                            }}
                          >
                            {user.initials}
                          </MDAvatar>
                        )}
                        <MDBox flex={1}>
                          <MDTypography variant="button" fontWeight="bold">
                            {user.name}
                          </MDTypography>
                          <MDTypography variant="caption" color="text" display="block">
                            {user.email}
                          </MDTypography>
                          <MDBox mt={1} display="flex" flexWrap="wrap" gap={1}>
                            {user.badges.map((t) => (
                              <MDBox
                                key={t}
                                px={1.2}
                                py={0.35}
                                borderRadius="lg"
                                sx={{
                                  backgroundColor: "rgba(229, 10, 70, 1)",
                                  color: "#fff",
                                  fontSize: "0.75rem",
                                  lineHeight: 1.2,
                                }}
                              >
                                {t}
                              </MDBox>
                            ))}
                          </MDBox>
                        </MDBox>
                      </MDBox>
                    </Card>
                  ))}
                </MDBox>

                <MDTypography variant="h6" fontWeight="bold" sx={{ ...sectionTitleSx, mt: 2 }}>
                  Most Used Tags:
                </MDTypography>
                <Card>
                  <MDBox p={2}>
                    <MDTypography variant="caption" color="text" display="block" mb={1.5}>
                      Top tags used across active users.
                    </MDTypography>
                    <MDBox display="flex" flexWrap="wrap" gap={1}>
                      {tags.map((t) => (
                        <MDBox
                          key={t.key}
                          px={1.2}
                          py={0.45}
                          borderRadius="lg"
                          sx={{
                            backgroundColor: "rgba(229, 10, 70, 1)",
                            color: "#fff",
                            fontSize: "0.75rem",
                            lineHeight: 1.2,
                          }}
                        >
                          {t.label}
                        </MDBox>
                      ))}
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>
            </Grid>
          </>
        ) : null}
      </MDBox>
    </DashboardLayout>
  );
}

export default Analytics;
